import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { ChevronDownIcon } from '~components/icons'
import type {
  BerthInfo,
  PortGroup,
  TerminalGroup,
} from '../utils/group-port-info'
import { BerthDetail } from './berth-detail'
import { COLORS } from '~theme/colors'
import { useDateFormat } from '~hooks/use-date-format'
import { formatDate } from '~utils/dates'

interface TerminalDetailProps {
  port: PortGroup | undefined
  terminal: TerminalGroup | undefined
  selectedBerthName: string | null
  onSelectBerth: (berthName: string) => void
}

export function TerminalDetail({
  port,
  terminal,
  selectedBerthName,
  onSelectBerth,
}: TerminalDetailProps) {
  const datePattern = useDateFormat('literal')

  if (!port || !terminal) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <Typography variant="paragraph2" color={COLORS.greys.dark}>
          Select a port and terminal to view berths
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ p: 3, borderBottom: 1, borderColor: COLORS.greys.light }}>
        <Typography variant="h2">
          {terminal.terminalName}
          <Typography
            component="span"
            variant="h4"
            sx={{ color: COLORS.greys.dark, ml: 1 }}
          >
            - {port.portName} ({port.portCode}) | {port.portCountry}
          </Typography>
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        <Typography variant="paragraphBold1">Berths visited:</Typography>

        <Box sx={{ mt: 3 }}>
          {terminal.berths.map((berth) => {
            const latestVisit = berth.visits[0]
            const isExpanded = berth.berthName === selectedBerthName

            return (
              <Accordion
                key={berth.berthName}
                variant="secondary"
                expanded={isExpanded}
                onChange={(_, expanded) => {
                  onSelectBerth(expanded ? berth.berthName : '')
                }}
                sx={{ mb: 3 }}
              >
                <AccordionSummary
                  expandIcon={<ChevronDownIcon width={20} height={20} />}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', pr: 1 }}
                  >
                    <Typography variant="paragraphBold2">
                      {berth.berthName}
                    </Typography>
                    <Typography variant="caption" color={COLORS.greys.dark}>
                      Last visited:{' '}
                      {formatDate(latestVisit.dateOfArrival, datePattern)}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {isExpanded && (
                    <BerthAccordionContent
                      berth={berth}
                      datePattern={datePattern}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

interface BerthAccordionContentProps {
  berth: BerthInfo
  datePattern: string
}

function BerthAccordionContent({
  berth,
  datePattern,
}: BerthAccordionContentProps) {
  const [tabIndex, setTabIndex] = useState(0)
  const [expandedHistoryVisit, setExpandedHistoryVisit] = useState<
    string | null
  >(null)
  const hasHistory = berth.visits.length > 1
  const latestVisit = berth.visits[0]

  return (
    <Box>
      <Tabs
        value={tabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: COLORS.greys.light }}
      >
        <Tab label="Latest Visit" />
        {hasHistory && <Tab label={`History (${berth.visits.length})`} />}
      </Tabs>

      {tabIndex === 0 && <BerthDetail visitId={latestVisit.visitId} />}

      {tabIndex === 1 && (
        <Box>
          {berth.visits.map((visit) => (
            <Accordion
              key={visit.visitId}
              variant="secondary"
              expanded={expandedHistoryVisit === visit.visitId}
              onChange={(_, expanded) => {
                setExpandedHistoryVisit(expanded ? visit.visitId : null)
              }}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ChevronDownIcon width={16} height={16} />}
              >
                <Typography variant="paragraph2">
                  {formatDate(visit.dateOfArrival, datePattern)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {expandedHistoryVisit === visit.visitId && (
                  <BerthDetail visitId={visit.visitId} />
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  )
}
